import { Component } from 'react';
import './ErrorBoundary.css';

// Stops one bad value from blanking the whole app.
//
// Without this, any render error unmounts everything and leaves a white page
// with nothing on it — and the person seeing it is as likely to be the partner
// overseas as the person who can read a console. This keeps the sidebar and
// the rest of the shell alive, names the section that failed, and offers the
// two things that actually help: try again, or go back to the Overview.
//
// A class component because `componentDidCatch` has no hook equivalent — this
// is the one place React still requires one.
export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Logged rather than reported anywhere: this app has no error service and
    // adding one would mean sending business data to a third party. The
    // console is enough for two people who can be asked to open it.
    console.error('Section failed to render:', error, info?.componentStack);
  }

  // Recovery is a real reset, not a reload: the failing page is remounted with
  // fresh state, so a bad row that has since been fixed elsewhere renders
  // cleanly without losing the session.
  retry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="boundary">
        <h2>This section couldn't be displayed</h2>
        <p>
          Something in {this.props.label || 'this page'} failed to load. Nothing has been changed or
          lost — the rest of the dashboard still works.
        </p>
        <p className="boundary-detail">{String(this.state.error?.message || this.state.error)}</p>
        <div className="boundary-actions">
          <button className="primary-action" onClick={this.retry}>
            Try again
          </button>
          <a className="ghost-button" href="/">
            Back to Overview
          </a>
        </div>
      </div>
    );
  }
}
